import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Building2,
  ExternalLink,
  Info,
  LifeBuoy,
  Loader2,
  MessageSquare,
  SlidersHorizontal,
} from 'lucide-react';

import { TicketPriorityBadge, TicketStatusBadge } from '@/components/support/TicketBadges';
import { TicketFact } from '@/components/support/TicketFact';
import { TicketReplyBox } from '@/components/support/TicketReplyBox';
import { TicketThread } from '@/components/support/TicketThread';
import { Checkbox } from '@/components/ui/Checkbox';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  TICKET_CATEGORY_LABEL,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
} from '@/constants/support';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadPrivateFile } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import type { TicketPriority, TicketStatus } from '@/types/support';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { platformSupportApi } from '../api/support.api';

const BACK = { to: '/platform/support', label: 'Back to tickets' };

/**
 * One ticket, worked from our side: the conversation on the left, where it
 * stands on the right.
 *
 * Status, priority and owner save the moment they are changed — three selects
 * and a Save button is one more click for the thing done most often. Each
 * change is audited on the server.
 */
export function SupportTicketWorkspacePage() {
  const { uuid = '' } = useParams();
  const [isInternal, setIsInternal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const threadContainerRef = useRef<HTMLElement>(null);

  const { data: ticket, isLoading, error, refetch } = useApiQuery(
    () => platformSupportApi.get(uuid),
    [uuid],
  );

  useEffect(() => {
    if (threadContainerRef.current) {
      threadContainerRef.current.scrollTop = threadContainerRef.current.scrollHeight;
    }
  }, [ticket?.messages?.length]);
  const staff = useApiQuery(() => platformSupportApi.staff(), []);

  async function change(changes: Parameters<typeof platformSupportApi.update>[1]) {
    setIsSaving(true);
    try {
      await platformSupportApi.update(uuid, changes);
      refetch();
    } catch (caught) {
      toast.error(
        'Ticket not updated',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  if (error) {
    return <QueryError title="Could not load this ticket" error={error} onRetry={refetch} />;
  }

  if (isLoading || !ticket) {
    return (
      <>
        <DeckPageHeader eyebrow="Support" title="Loading ticket…" description="Fetching the conversation." backTo={BACK} />
        <div className="card p-5">
          <SkeletonLines count={6} />
        </div>
      </>
    );
  }

  const staffOptions = [
    { value: '', label: 'Unassigned' },
    ...(staff.data ?? []).map((person) => ({ value: person.uuid, label: person.name })),
  ];

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <DeckPageHeader
        compact
        title={ticket.subject}
        description={`${ticket.institute.displayName} · ${TICKET_CATEGORY_LABEL[ticket.category]}`}
        backTo={BACK}
        mark={
          <LifeBuoy className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        }
        actions={
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
            <span className="inline-flex items-center gap-1.5 rounded-pill border border-accent-line bg-surface px-2.5 py-1 text-2xs font-bold uppercase tracking-label text-accent-2 shadow-2xs">
              <span className="relative grid h-2 w-2 place-items-center" aria-hidden>
                <span className="absolute inset-0 animate-glow rounded-full bg-accent" />
                <span className="h-1 w-1 rounded-full bg-accent" />
              </span>
              Support · {ticket.ticketNumber}
            </span>
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} always />
          </div>
        }
      />

      <div className="grid grid-cols-1 items-start gap-4 sm:gap-5 lg:grid-cols-3">
        {/* Main Conversation & Reply Area */}
        <div className="flex flex-col lg:col-span-2">
          <div className="card flex flex-col p-4 sm:p-5 shadow-xs border border-line bg-surface rounded-2xl h-[calc(100dvh-200px)] min-h-[520px]">
            {/* Header */}
            <div className="shrink-0 border-b border-line/70 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="grid h-7 w-7 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                  <MessageSquare className="h-4 w-4" />
                </span>
                <span className="text-sm font-bold text-ink">Conversation</span>
              </div>
              <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800/80 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                {ticket.messages.length} message{ticket.messages.length === 1 ? '' : 's'}
              </span>
            </div>

            {/* Scrollable messages thread */}
            <section
              ref={threadContainerRef}
              aria-label="Conversation"
              className="py-3 flex-1 min-h-0 overflow-y-auto pr-1.5 [scrollbar-width:thin]"
            >
              <TicketThread
                messages={ticket.messages}
                viewerSide="SUPPORT"
                onOpenAttachment={(attachment) =>
                  void downloadPrivateFile(
                    platformSupportApi.attachmentPath(ticket.uuid, attachment.uuid),
                    attachment.fileName,
                  ).catch(() => toast.error('File not opened', 'Please try again.'))
                }
              />
              <div ref={messagesEndRef} />
            </section>

            {/* Reply Input Box */}
            <section className="shrink-0 pt-3 border-t border-line/80 bg-surface" aria-labelledby="reply-heading">
              <div className="mb-2 flex items-center justify-between">
                <h2 id="reply-heading" className="text-xs font-bold uppercase tracking-wider text-ink-3">
                  {isInternal ? 'Internal Note' : 'Reply to the School'}
                </h2>
                {isInternal && (
                  <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                    Visible only to support team
                  </span>
                )}
              </div>
              {ticket.status === 'CLOSED' ? (
                <p className="text-sm text-ink-2 rounded-xl bg-surface-2 p-3 border border-line">
                  This ticket is closed. Change its status to reopen it before replying.
                </p>
              ) : (
                <TicketReplyBox
                  sendLabel={isInternal ? 'Add note' : 'Send reply'}
                  placeholder={
                    isInternal
                      ? 'A note for the support team — the school never sees this.'
                      : `Reply to ${ticket.raisedBy.name} at ${ticket.institute.displayName}…`
                  }
                  extra={
                    <Checkbox
                      label="Internal note (not sent to the school)"
                      checked={isInternal}
                      onChange={(event) => setIsInternal(event.target.checked)}
                    />
                  }
                  onSend={async (message, files) => {
                    const { emailed } = await platformSupportApi.reply(
                      ticket.uuid,
                      message,
                      isInternal,
                      files,
                    );
                    // The email is best-effort after the reply is saved, so the
                    // toast reports what the server says happened to it rather
                    // than promising a mail that may never have left.
                    toast.success(
                      isInternal ? 'Note added' : 'Reply posted',
                      isInternal
                        ? 'Only the support team can see it.'
                        : emailed
                          ? `Emailed to ${ticket.raisedBy.name}.`
                          : 'No email could be sent — they will see it on the ticket.',
                    );
                    setIsInternal(false);
                    refetch();
                  }}
                />
              )}
            </section>
          </div>
        </div>

        {/* Aside Sidebar: Manage & Details */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start">
          {/* Manage section */}
          <section
            className="card flex flex-col gap-3.5 p-4 sm:p-5 rounded-2xl shadow-xs border border-line bg-surface"
            aria-labelledby="manage-heading"
            aria-busy={isSaving}
          >
            <div className="flex items-center justify-between border-b border-line/70 pb-3">
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                  <SlidersHorizontal className="h-4 w-4" />
                </span>
                <h2 id="manage-heading" className="text-sm font-bold text-ink">
                  Manage
                </h2>
              </div>
              {isSaving && (
                <span className="flex items-center gap-1.5 text-xs text-accent font-medium">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving…
                </span>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Select
                label="Status"
                options={TICKET_STATUS_OPTIONS}
                value={ticket.status}
                disabled={isSaving}
                hint={ticket.status === 'WAITING_ON_SCHOOL' ? 'Moves back to In progress when the school replies.' : undefined}
                onChange={(event) => void change({ status: event.target.value as TicketStatus })}
              />
              <Select
                label="Priority"
                options={TICKET_PRIORITY_OPTIONS}
                value={ticket.priority}
                disabled={isSaving}
                onChange={(event) => void change({ priority: event.target.value as TicketPriority })}
              />
              <Select
                label="Owner"
                options={staffOptions}
                value={ticket.assignedTo?.uuid ?? ''}
                disabled={isSaving || staff.isLoading}
                error={staff.error ? 'Could not load the support team' : undefined}
                onChange={(event) => void change({ assignedToUuid: event.target.value || null })}
              />
            </div>
          </section>

          {/* Details section */}
          <section
            className="card flex flex-col gap-3.5 p-4 sm:p-5 rounded-2xl shadow-xs border border-line bg-surface text-sm"
            aria-labelledby="details-heading"
          >
            <div className="flex items-center gap-2 border-b border-line/70 pb-3">
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                <Info className="h-4 w-4" />
              </span>
              <h2 id="details-heading" className="text-sm font-bold text-ink">
                Details
              </h2>
            </div>

            <Link
              to={`/platform/institutes/${ticket.institute.uuid}`}
              className="group flex items-center justify-between rounded-xl border border-line bg-surface-2 p-2.5 transition-all hover:border-accent hover:bg-accent-soft/30 hover:shadow-2xs"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface text-accent border border-line/60">
                  <Building2 className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-xs font-bold text-ink group-hover:text-accent transition-colors">
                    {ticket.institute.displayName}
                  </p>
                  <p className="text-[11px] text-ink-3">Institute profile</p>
                </div>
              </div>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-3 group-hover:text-accent transition-colors" />
            </Link>

            <dl className="flex flex-col">
              <TicketFact label="Category" value={TICKET_CATEGORY_LABEL[ticket.category]} />
              <TicketFact label="Raised by" value={ticket.raisedBy.name} />
              <TicketFact label="Raised date" value={formatDateTime(ticket.createdAt)} />
              <TicketFact label="Last activity" value={formatDateTime(ticket.lastActivityAt)} />
              {ticket.resolvedAt && <TicketFact label="Resolved" value={formatDateTime(ticket.resolvedAt)} />}
              {ticket.closedAt && <TicketFact label="Closed" value={formatDateTime(ticket.closedAt)} />}
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}
